import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandMark from './BrandMark';

export type SitePage = 'privacy' | 'terms' | 'home' | 'contact';

interface SiteNavigationProps {
  currentPage?: SitePage;
}

interface NavItem {
  label: string;
  to: string;
  section?: string;
}

const landingItems: NavItem[] = [
  { label: 'How it works', to: '/#how-it-works', section: 'how-it-works' },
  { label: 'Features', to: '/#features', section: 'features' },
  { label: 'Mobile', to: '/#mobile', section: 'mobile' },
  { label: 'Pricing', to: '/#pricing', section: 'pricing' },
  { label: 'FAQ', to: '/#faq', section: 'faq' },
];

const pageItems: Record<Exclude<SitePage, 'home'>, NavItem[]> = {
  privacy: [
    { label: 'Home', to: '/' },
    { label: 'Terms', to: '/terms-and-conditions' },
    { label: 'Contact', to: '/contact' },
  ],
  terms: [
    { label: 'Home', to: '/' },
    { label: 'Privacy', to: '/privacy-policy' },
    { label: 'Contact', to: '/contact' },
  ],
  contact: [
    { label: 'Home', to: '/' },
    { label: 'Privacy', to: '/privacy-policy' },
    { label: 'Terms', to: '/terms-and-conditions' },
  ],
};

const SiteNavigation: React.FC<SiteNavigationProps> = ({ currentPage = 'home' }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const items = currentPage === 'home' ? landingItems : pageItems[currentPage];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleItemClick = (item: NavItem) => {
    setIsMobileMenuOpen(false);
    if (currentPage === 'home' && item.section) {
      document.getElementById(item.section)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-200 ${
        isScrolled || isMobileMenuOpen
          ? 'border-slate-200/90 bg-white/95 shadow-[0_1px_8px_rgba(24,32,28,0.04)] backdrop-blur-xl'
          : 'border-transparent bg-white/80 backdrop-blur-md'
      }`}
    >
      <div className="site-container flex h-16 items-center justify-between">
        <BrandMark />

        <nav className="hidden items-center gap-1 md:flex" aria-label="Primary navigation">
          {items.map((item) =>
            currentPage === 'home' && item.section ? (
              <button
                key={item.label}
                type="button"
                onClick={() => handleItemClick(item)}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                {item.label}
              </button>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                {item.label}
              </Link>
            )
          )}
          <a
            href="https://1medicine.app"
            target="_blank"
            rel="noopener noreferrer"
            className="primary-button ml-3 !min-h-[2.35rem] !px-4"
          >
            Open 1MED
            <span aria-hidden="true">↗</span>
          </a>
        </nav>

        <button
          type="button"
          onClick={() => setIsMobileMenuOpen((open) => !open)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 md:hidden"
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-navigation"
          aria-label={isMobileMenuOpen ? 'Close navigation' : 'Open navigation'}
        >
          {isMobileMenuOpen ? (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          ) : (
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path strokeLinecap="round" d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          )}
        </button>
      </div>

      {isMobileMenuOpen && (
        <nav
          id="mobile-navigation"
          className="border-t border-slate-200 bg-white px-4 pb-5 pt-3 shadow-xl md:hidden"
          aria-label="Mobile navigation"
        >
          <div className="mx-auto flex max-w-xl flex-col gap-1">
            {items.map((item) =>
              currentPage === 'home' && item.section ? (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => handleItemClick(item)}
                  className="rounded-lg px-3 py-3 text-left text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="rounded-lg px-3 py-3 text-base font-medium text-slate-700 hover:bg-slate-100"
                >
                  {item.label}
                </Link>
              )
            )}
            <a
              href="https://1medicine.app"
              target="_blank"
              rel="noopener noreferrer"
              className="primary-button mt-2 w-full"
            >
              Open 1MED
              <span aria-hidden="true">↗</span>
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default SiteNavigation;
